import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import accountIcon from "../images/icons/account-icon.svg";
import signOutIcon from "../images/icons/sign-out-icon.svg";
import dropdownArrow from "../images/icons/dropdown-arrow-icon.svg";
import authService from "../services/authService";
import { clearActiveMasterAccountId } from "../tenant/activeMasterAccount";
import {
  clearImpersonationSession,
  loadImpersonationSession,
} from "../supportMode/supportModeSession";
import {
  isSelectedUserCanonicalChrome,
  resolveImpersonationDisplayIdentity,
} from "../supportMode/impersonationIdentity";
import { fetchMyProfile, mapProfileToForm } from "../services/accountService";
import {
  AVATAR_SOURCE_AUTH0_FALLBACK,
  PROFILE_CHROME_SOURCE_KK_PROFILE,
  hasUsableAvatarIdentity,
  resolveAuthenticatedDisplayIdentity,
  unresolvedAvatarInitialsFallback,
} from "../auth/profileIdentity";
import { resolveMasterShellHeaderLabel } from "./MasterLayout.headerLabel";
import {
  BM_ACCOUNT_BASE,
  hostsGlobalAccountInBrandManager,
} from "../brandManagerPaths";
import { useOptionalTenant } from "../tenant/TenantContext";
import { isHttpAvatarUrl } from "../utils/profileAvatar";
import "./AuthenticatedUserMenu.css";

function supportModeIdentityReady(session) {
  if (!session || typeof session !== "object") return false;
  const email = String(session.userEmail || "").trim();
  const display = String(session.userDisplayName || "").trim();
  if (email) return true;
  if (display && !/^auth0\|/i.test(display)) return true;
  return false;
}

/**
 * Shared authenticated-user chrome for every MA top shell.
 * Never driven by Brand / Master Account company data.
 */
export default function AuthenticatedUserMenu() {
  const navigate = useNavigate();
  const {
    userInfo,
    identityReady,
    updateUserInfo,
    canonicalProfile,
    hydrateCanonicalProfile,
  } = useUser();
  const tenantCtx = useOptionalTenant();
  const profilePath = hostsGlobalAccountInBrandManager(tenantCtx?.tenant?.kind)
    ? BM_ACCOUNT_BASE
    : "/master-account/account";
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const dropdownRef = useRef(null);
  // Bounded signed-URL recovery (direct login, kk-profile avatar only):
  // ONE canonical re-hydration per failed display URL. The URL produced by a
  // recovery is never itself recovered again → no retry loop.
  const recoveryRef = useRef({ sub: null, failedUrl: null, recoveredUrl: null });
  // Support Mode: one canonical /me/profile read per selected user per shell
  // mount; the result is cached so UserContext re-hydration (storage events)
  // can be re-applied without another API call.
  const selectedUserChromeRef = useRef({ sub: null, chrome: null });

  const impersonationSession = loadImpersonationSession();
  const impersonatedUserSub = impersonationSession?.impersonatedUserSub || null;

  // Support Mode identity source (display only — never authorization):
  //   • canonical: kk-profile chrome fetched for THIS session's selected user
  //   • otherwise: the SA handoff session (name/email → initials)
  // Never the operator's Auth0 `auth_user` / stale localStorage profile.
  const selectedUserChrome =
    impersonationSession &&
    isSelectedUserCanonicalChrome(userInfo, impersonationSession)
      ? userInfo
      : null;

  // Once the selected user's canonical kk-profile chrome is bound, name/email
  // follow it (same as direct login after My Profile sync); until then the
  // handoff session supplies name → initials.
  const canonicalSelectedIdentity =
    selectedUserChrome &&
    hasUsableAvatarIdentity({
      firstName: selectedUserChrome.firstName,
      lastName: selectedUserChrome.lastName,
      email: selectedUserChrome.email,
    })
      ? resolveAuthenticatedDisplayIdentity(selectedUserChrome)
      : null;

  const headerLabel = canonicalSelectedIdentity
    ? canonicalSelectedIdentity.headerLabel
    : resolveMasterShellHeaderLabel({
        userInfo,
        impersonationSession,
      });

  const displayIdentity = impersonationSession
    ? canonicalSelectedIdentity ||
      resolveImpersonationDisplayIdentity(impersonationSession, null)
    : resolveAuthenticatedDisplayIdentity(userInfo);

  // Direct login: userInfo — canonical kk-profile chrome once
  // CanonicalProfileHydrator has run (avatarUrl in memory), Auth0 picture as
  // the legitimate fallback before/without a canonical avatar.
  // Support Mode: ONLY the selected user's canonical kk-profile chrome.
  const avatarSource = impersonationSession
    ? selectedUserChrome?.avatar
    : userInfo?.avatar;
  const directCanonicalChrome =
    !impersonationSession &&
    userInfo?.profileSource === PROFILE_CHROME_SOURCE_KK_PROFILE;
  // While the canonical self read is in flight and chrome is still Auth0 /
  // stored-derived, show the neutral loading face rather than a picture the
  // canonical profile may immediately supersede.
  const canonicalPending =
    !impersonationSession &&
    canonicalProfile?.status === "pending" &&
    !directCanonicalChrome;
  const avatarUrl =
    isHttpAvatarUrl(avatarSource) && !avatarFailed && !canonicalPending
      ? String(avatarSource).trim()
      : null;

  const handleAvatarError = () => {
    setAvatarFailed(true);
    const failedUrl = isHttpAvatarUrl(avatarSource)
      ? String(avatarSource).trim()
      : null;
    if (
      !failedUrl ||
      !directCanonicalChrome ||
      userInfo?.avatarSource === AVATAR_SOURCE_AUTH0_FALLBACK ||
      typeof hydrateCanonicalProfile !== "function"
    ) {
      return;
    }
    const rec = recoveryRef.current;
    const sub = userInfo?.sub || null;
    if (rec.sub !== sub) {
      recoveryRef.current = { sub, failedUrl: null, recoveredUrl: null };
    }
    const current = recoveryRef.current;
    // Already recovered once for this URL, or this URL IS the recovery
    // result → fall back to initials (no loop).
    if (current.failedUrl === failedUrl || current.recoveredUrl === failedUrl) {
      return;
    }
    recoveryRef.current = { ...current, failedUrl };
    Promise.resolve(hydrateCanonicalProfile({ force: true }))
      .then((chrome) => {
        const fresh = isHttpAvatarUrl(chrome?.avatar)
          ? String(chrome.avatar).trim()
          : null;
        recoveryRef.current = { ...recoveryRef.current, recoveredUrl: fresh };
      })
      .catch(() => {
        /* fail soft: initials remain */
      });
  };

  useEffect(() => {
    if (!impersonatedUserSub || selectedUserChrome) return undefined;
    const cached = selectedUserChromeRef.current;
    if (cached.sub === impersonatedUserSub) {
      // Already fetched for this selected user; re-apply (no extra API call)
      // if UserContext was re-hydrated from auth_user / storage events.
      if (cached.chrome && updateUserInfo) updateUserInfo(cached.chrome);
      return undefined;
    }
    selectedUserChromeRef.current = { sub: impersonatedUserSub, chrome: null };
    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchMyProfile();
        if (cancelled) return;
        const form = mapProfileToForm(payload?.profile);
        // UserContext binds this to the selected user's sub (Support Mode).
        const chrome = {
          firstName: form.firstName || "",
          lastName: form.lastName || "",
          email: form.email || "",
          avatar: form.avatar || null,
          phoneNumber: form.phoneNumber || "",
          profileSource: PROFILE_CHROME_SOURCE_KK_PROFILE,
        };
        selectedUserChromeRef.current = { sub: impersonatedUserSub, chrome };
        if (updateUserInfo) updateUserInfo(chrome);
      } catch {
        // Fail soft: header keeps the handoff-session initials.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [impersonatedUserSub, selectedUserChrome, updateUserInfo]);

  const avatarIdentityReady = impersonationSession
    ? supportModeIdentityReady(impersonationSession)
    : Boolean(identityReady) &&
      hasUsableAvatarIdentity({
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
        email: userInfo?.email,
      });

  const showAvatarLoading =
    !avatarUrl && (!avatarIdentityReady || canonicalPending);
  const initials =
    displayIdentity.initials ||
    (avatarIdentityReady ? unresolvedAvatarInitialsFallback() : "");

  useEffect(() => {
    // New display URL (hydration, upload, recovery) → retry rendering it.
    setAvatarFailed(false);
  }, [userInfo?.avatar, userInfo?.sub]);

  useEffect(() => {
    // Authenticated sub changed → recovery budget starts over.
    recoveryRef.current = { sub: null, failedUrl: null, recoveredUrl: null };
  }, [userInfo?.sub]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    clearImpersonationSession();
    clearActiveMasterAccountId();
    await authService.logout();
  };

  const renderAvatarFace = ({ initialsTestId, loadingTestId }) => {
    if (avatarUrl) {
      return (
        <img
          src={avatarUrl}
          alt=""
          className="company-nav-image"
          data-testid={
            initialsTestId === "ma-header-avatar-initials"
              ? "ma-header-avatar-image"
              : "ma-profile-dropdown-image"
          }
          onError={handleAvatarError}
        />
      );
    }
    if (showAvatarLoading) {
      return (
        <div
          className="company-nav-avatar-loading"
          data-testid={loadingTestId}
          aria-hidden="true"
        />
      );
    }
    return (
      <div
        className="company-nav-initials"
        data-testid={initialsTestId}
        aria-hidden="true"
      >
        {initials}
      </div>
    );
  };

  return (
    <div className="user-nav" ref={dropdownRef} data-testid="ma-user-menu">
      <span
        className={`user-menu ${isDropdownOpen ? "open" : ""}`}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={isDropdownOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsDropdownOpen((v) => !v);
          }
        }}
      >
        <div
          className="company-nav-avatar"
          data-testid="ma-header-avatar"
          aria-busy={showAvatarLoading || undefined}
        >
          {renderAvatarFace({
            initialsTestId: "ma-header-avatar-initials",
            loadingTestId: "ma-header-avatar-loading",
          })}
        </div>
        <span data-testid="ma-header-user-label">{headerLabel}</span>
        <span className="arrow-container">
          <img src={dropdownArrow} alt="" className="dropdown-arrow" />
        </span>
      </span>

      {isDropdownOpen && (
        <div className="kk-user-dropdown" role="menu">
          <div className="kk-user-dropdown__header">
            <div className="kk-user-dropdown__profile-container">
              <div className="kk-user-dropdown__avatar">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="kk-user-dropdown__profile-image"
                    onError={handleAvatarError}
                  />
                ) : showAvatarLoading ? (
                  <div
                    className="kk-user-dropdown__initials company-nav-avatar-loading"
                    data-testid="ma-profile-dropdown-loading"
                    aria-hidden="true"
                  />
                ) : (
                  <div
                    className="kk-user-dropdown__initials"
                    data-testid="ma-profile-dropdown-initials"
                  >
                    {initials}
                  </div>
                )}
              </div>
            </div>
            <div className="kk-user-dropdown__info">
              <div
                className="kk-user-dropdown__greeting"
                data-testid="ma-profile-dropdown-name"
              >
                {displayIdentity.fullName || displayIdentity.headerLabel}
              </div>
              <div
                className="kk-user-dropdown__email"
                data-testid="ma-profile-dropdown-email"
              >
                {displayIdentity.email || "—"}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="kk-user-dropdown__link"
            onClick={() => {
              navigate(profilePath, {
                state: { activeTab: "profile" },
              });
              setIsDropdownOpen(false);
            }}
          >
            <img src={accountIcon} alt="" className="submenu-icon" />
            My Profile
          </button>
          <button
            type="button"
            className="kk-user-dropdown__sign-out"
            onClick={handleSignOut}
          >
            <img src={signOutIcon} alt="" className="sign-out-icon" />
            Sign Out
          </button>
          <div className="kk-user-dropdown__footer">
            <a href="#" className="kk-user-dropdown__link">
              Terms and Conditions
            </a>
            <span>•</span>
            <a href="#" className="kk-user-dropdown__link">
              Privacy Policy
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
